import type {
  ConfigurationModuleResourceReadBffV2Api,
  EnumLogicalDeviceRoleApi,
  EnumOutputFileFormatApi,
  ParameterSetListOutputFilesSearchBffV2Api,
  ParameterSetsOutputLogicalBffV2Api,
  SearchRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { searchFilterFilterType, searchOperator } from '@ingenico/api-sdk-device-management-private-v2';
import { isEmpty, isEqual, toString } from 'lodash-es';
import type {
  ParameterFileFormat,
  ParameterOutputFileDevice,
  ParameterOutputFileModalModel,
} from '../../../components/parameter-output-file-modal/ParameterOutputFileModal.model';
import { CoreError } from './coreErrorConstructor';

interface LogicalDeviceOutputFileApi {
  readonly fileFormat?: EnumOutputFileFormatApi;
  readonly fileName?: string;
  readonly logicalDeviceId?: string;
  readonly logicalDeviceName?: string;
  readonly logicalDeviceRole?: EnumLogicalDeviceRoleApi;
  readonly logicalDeviceRoleSnapshot?: EnumLogicalDeviceRoleApi;
}

interface OutputFilenamesEntryApi {
  readonly parameterSetId: string;
  readonly templateId: string;
  readonly templateName?: string;
  readonly outputFiles: LogicalDeviceOutputFileApi[];
}

export type ParameterOutputFilenamesApi =
  | ParameterSetsOutputLogicalBffV2Api
  | ParameterSetListOutputFilesSearchBffV2Api;

export interface MergeParameterOutputFileModalDefaultProps {
  readonly configurationModule: ConfigurationModuleResourceReadBffV2Api;
  readonly outputFilenames: ParameterOutputFilenamesApi;
}
export interface MergeParameterOutputFileModalReturn extends MergeParameterOutputFileModalDefaultProps {
  readonly transform: ParameterOutputFileModalModel[];
}

export interface MergeParameterOutputFileModalInputProps extends MergeParameterOutputFileModalDefaultProps {
  readonly errorPrefix?: string;
}
export function mergeParameterOutputFileModal({
  configurationModule,
  outputFilenames,
  errorPrefix = 'DEPLOYMENT-UNIT',
}: MergeParameterOutputFileModalInputProps): MergeParameterOutputFileModalReturn {
  const { parameterSets } = configurationModule;
  const outputFilenamesData: OutputFilenamesEntryApi[] = outputFilenames.data;

  if (parameterSets.length <= 0) {
    throw new CoreError('emptyMessage', 400, {
      type: `CUSTOM-${errorPrefix}-ERR-001`,
      title: 'Custom error: ParamterSets are empty',
    });
  }

  const initialTransform: ParameterOutputFileModalModel[] = parameterSets.map(({ id: parameterSetId, templateId }) => {
    const outputData = outputFilenamesData.find(
      (outputFilename) =>
        isEqual(outputFilename.parameterSetId, parameterSetId) && isEqual(outputFilename.templateId, templateId),
    );
    const devicesById = new Map<string | undefined, ParameterOutputFileDevice>();

    outputData?.outputFiles.forEach((outputFile) => {
      const { logicalDeviceId, logicalDeviceName, logicalDeviceRole, fileFormat, fileName } = outputFile;
      const logicalDeviceRoleSnapshot = outputFile.logicalDeviceRoleSnapshot ?? logicalDeviceRole;
      const existingDevice = devicesById.get(logicalDeviceId);
      const outputFiles = [
        ...(existingDevice?.outputFiles ?? []),
        {
          fileFormat: fileFormat as ParameterFileFormat,
          fileName,
        },
      ];

      devicesById.set(logicalDeviceId, {
        logicalDeviceId,
        logicalDeviceName,
        logicalDeviceRoleSnapshot,
        outputFiles,
      });
    });
    const devices = [...devicesById.values()];
    return {
      parameterSetId,
      templateId,
      templateName: outputData?.templateName ?? '',
      devices,
    };
  });

  const transform = initialTransform.filter(({ devices }) => devices.some(({ outputFiles }) => outputFiles.length > 0));

  if (transform.length <= 0) {
    throw new CoreError('emptyMessage', 400, {
      type: `CUSTOM-${errorPrefix}-ERR-001`,
      title: 'Custom error: ParamterSets mapping are empty for output files',
    });
  }
  return {
    configurationModule,
    outputFilenames,
    transform,
  };
}

export interface MapSearchOutputFilenamesRequestInput {
  readonly parameterSetIds: string[];
  readonly logicalDeviceId?: string;
}

export function mapSearchOutputFilenamesRequest({
  parameterSetIds,
  logicalDeviceId,
}: MapSearchOutputFilenamesRequestInput): SearchRequestApi {
  return {
    filters: [
      {
        filterType: searchFilterFilterType.array,
        name: 'outputFilenames.parameterSetId',
        operator: searchOperator.in,
        value: JSON.stringify(parameterSetIds),
      },
      ...(isEmpty(logicalDeviceId)
        ? []
        : [
            {
              filterType: searchFilterFilterType.string,
              name: 'outputFilenames.logicalDeviceId',
              operator: searchOperator.equals,
              value: toString(logicalDeviceId),
            },
          ]),
    ],
    sort: {},
  };
}
